/* Local recovery never creates a cart line or a design request. */
(() => {
  if (globalThis.HeirloomDraftRecovery) return;
  const ttl = 12 * 60 * 60 * 1000, prefix = 'personalization-local:';
  let database;
  const open = () => database ||= new Promise((resolve, reject) => {
    const request = indexedDB.open('3dh-preview-request-v1', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('requests');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  const read = async key => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const request = db.transaction('requests', 'readonly').objectStore('requests').get(prefix + key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };
  const write = async (keys, value) => {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('requests', 'readwrite'), store = tx.objectStore('requests');
      keys.forEach(key => store.put(value, prefix + key));
      // Remove expired photos as part of the same local write.
      const cursor = store.openCursor();
      cursor.onsuccess = () => {
        const row = cursor.result;
        if (!row) return;
        if (String(row.key).startsWith(prefix) && row.value.expiresAt <= Date.now()) row.delete();
        row.continue();
      };
      tx.oncomplete = resolve;
      tx.onerror = tx.onabort = () => reject(tx.error);
    });
  };
  const fingerprint = async value => {
    if (value instanceof Blob) {
      const bytes = await crypto.subtle.digest('SHA-256', await value.arrayBuffer());
      return { type: value.type, bytes: value.size, hash: Array.from(new Uint8Array(bytes), n => n.toString(16).padStart(2, '0')).join('') };
    }
    if (Array.isArray(value)) return Promise.all(value.map(fingerprint));
    if (value && typeof value === 'object') {
      const result = {};
      for (const key of Object.keys(value).sort()) result[key] = await fingerprint(value[key]);
      return result;
    }
    return value;
  };
  const signatureOf = async payload => {
    // Upload receipts describe storage, not the photo or personalization chosen by the customer.
    const value = Array.isArray(payload.photos) ? { ...payload, photos: payload.photos.map(photo => {
      const copy = { ...photo }; delete copy.asset; delete copy.assetRequestId; return copy;
    }) } : payload;
    return JSON.stringify(await fingerprint(value));
  };
  const connect = ({ productHandle, capture, restore }) => {
    const latest = 'product:' + productHandle;
    let context = new URL(location.href).searchParams.get('customization');
    let record = null, restoring = true, queue = Promise.resolve(), restoreFailed = false;
    const ready = (async () => {
      try {
        record = await read(context ? 'cart:' + context : latest);
        if (record?.productHandle !== productHandle || record.expiresAt <= Date.now()) record = null;
        if (record && !await restore(structuredClone(record.payload))) { record = null; restoreFailed = true; }
        if (record) record.signature = await signatureOf(record.payload);
      } catch { record = null; restoreFailed = true; }
      finally { restoring = false; }
    })();
    const persist = async (receipt, payload) => {
      if (restoring) return;
      try {
        const signature = await signatureOf(payload);
        const cart = receipt || (record?.signature === signature ? record.cart : null);
        const next = { productHandle, payload, signature, cart, expiresAt: Date.now() + ttl };
        const keys = [latest];
        if (receipt?.requestId) keys.push('cart:' + receipt.requestId);
        if (receipt?.id) keys.push('cart:' + receipt.id);
        await write(keys, next);
        if (context && record?.cart && !receipt && record.signature !== signature) {
          // An edited draft is a new configuration; keep the original cart snapshot intact.
          const url = new URL(location.href);
          if (url.searchParams.get('customization') === context) {
            url.searchParams.delete('customization');
            history.replaceState(history.state, '', url.href);
          }
          context = null;
        }
        record = next;
      } catch { /* A browser storage failure must not turn a successful purchase save into a failure. */ }
    };
    const save = () => {
      if (restoring) return Promise.resolve();
      try {
        const payload = structuredClone(capture());
        queue = queue.then(() => persist(null, payload));
      } catch { /* Recovery is optional when this browser cannot store files. */ }
      return queue;
    };
    const remember = async result => {
      await ready;
      const receipt = { requestId: result?.requestId || '', id: result?.id || '' };
      if (!receipt.requestId && !receipt.id) return;
      try {
        const payload = structuredClone(capture());
        queue = queue.then(() => persist(receipt, payload));
      } catch { return; }
      await queue;
    };
    const reuse = async ({ purchase = false } = {}) => {
      await ready; await queue;
      if (context && (!record || restoreFailed)) throw new Error('We could not restore this item on this device. Your cart is unchanged. Please return to your cart or contact us for help.');
      if (!record?.cart) {
        if (context) throw new Error('These details have changed. Return to your cart to select the original item before requesting its preview.');
        return null;
      }
      const signature = await signatureOf(structuredClone(capture()));
      if (signature !== record.signature) {
        if (context) throw new Error('These details differ from the item in your cart. Return to the original item before requesting its preview.');
        return null;
      }
      const saved = record;
      const base = globalThis.Shopify?.routes?.root || '/';
      const response = await fetch(base + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
      const cart = await response.json();
      const match = cart.items?.find(item => item.properties?.['_3D Heirloom Request']
        ? item.properties['_3D Heirloom Request'] === saved.cart.requestId
        : saved.cart.id && item.properties?._design_id === saved.cart.id);
      if (!match) throw new Error('This item is no longer in your cart. If you already paid, please check your order instead.');
      if (saved.expiresAt <= Date.now() || await signatureOf(structuredClone(capture())) !== saved.signature) throw new Error('These details have changed. Return to your cart to select the original item.');
      if (purchase) {
        const properties = match.properties || {};
        const options = saved.payload.options || {};
        const expected = { ...saved.cart, server: Boolean(properties['_3D Heirloom Revision']), revisionId: properties['_3D Heirloom Revision'], variant: String(match.variant_id || match.id), variantId: String(match.variant_id || match.id), quantity: Number(match.quantity), sku: match.sku || '', fields: properties, properties, baseId: options.baseVariantId || '', baseVariantId: options.baseVariantId || '', careId: options.careId || '', careVariantId: options.careId || '', careQuantity: Number(match.quantity) };
        return { cart, item: match, expected };
      }
      return { ...saved.cart };
    };
    return { ready, save, remember, reuse, get context() { return context; }, reusePurchase: async () => {
      await ready; await queue;
      return context ? reuse({ purchase: true }) : null;
    } };
  };
  globalThis.HeirloomDraftRecovery = { connect };
})();
